import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calculator, Plus, Trash2, Package, AlertTriangle, CheckCircle2, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'SA', name: 'السعودية (Zone L)' },
  { code: 'AE', name: 'الإمارات (Zone L)' },
  { code: 'BH', name: 'البحرين (Zone L)' },
  { code: 'KW', name: 'الكويت (Zone E)' },
  { code: 'OM', name: 'عُمان (Zone E)' },
  { code: 'QA', name: 'قطر (Zone E)' },
  { code: 'JO', name: 'الأردن (Zone E)' },
  { code: 'EG', name: 'مصر (Zone E)' },
  { code: 'MA', name: 'المغرب (Zone E)' },
  { code: 'IQ', name: 'العراق (Zone J)' },
  { code: 'LB', name: 'لبنان (Zone J)' },
  { code: 'YE', name: 'اليمن (Zone J)' },
];

const emptyItem = () => ({ sku: '', quantity: 1, grams: 0 });

const StatusBadge = ({ status }) => {
  const cfg = {
    ok: { bg: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30', label: 'نجاح' },
    skus_not_in_db: { bg: 'bg-amber-500/15 text-amber-600 border-amber-500/30', label: 'SKU غير موجود' },
    zone_not_configured: { bg: 'bg-rose-500/15 text-rose-600 border-rose-500/30', label: 'منطقة غير مُدعَّمة' },
    rate_lookup_failed: { bg: 'bg-rose-500/15 text-rose-600 border-rose-500/30', label: 'فشل جدول الأسعار' },
    db_error: { bg: 'bg-rose-500/15 text-rose-600 border-rose-500/30', label: 'خطأ في قاعدة البيانات' },
  };
  const c = cfg[status] || { bg: 'bg-gray-500/15 text-gray-600 border-gray-500/30', label: status };
  return <Badge className={`${c.bg} border`} data-testid="status-badge">{c.label}</Badge>;
};

const RateCalculator = () => {
  const [items, setItems] = useState([emptyItem()]);
  const [country, setCountry] = useState('SA');
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };

  const handleCalculate = async () => {
    const validItems = items
      .map(it => ({
        sku: (it.sku || '').trim(),
        quantity: Math.max(1, parseInt(it.quantity) || 1),
        grams: Math.max(0, parseInt(it.grams) || 0),
      }))
      .filter(it => it.sku);

    if (validItems.length === 0) {
      toast.error('أدخل على الأقل SKU واحد');
      return;
    }

    setCalculating(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/debug/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: validItems,
          destination_country: country,
          currency: 'SAR',
        }),
      });
      const data = await res.json();
      setResult(data);
      if (data.ok) toast.success(`تم حساب السعر: ${data.calculation.total_rate} ر.س`);
      else toast.warning(`الحالة: ${data.status}`);
    } catch (e) {
      toast.error(`فشل الاتصال: ${e.message}`);
      setResult({ ok: false, status: 'network_error', error: e.message });
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setItems([emptyItem()]);
    setCountry('SA');
    setResult(null);
  };

  const copyResult = () => {
    if (!result) return;
    const text = JSON.stringify(result, null, 2);
    navigator.clipboard.writeText(text);
    toast.success('نُسخ JSON');
  };

  const calc = result?.calculation;
  const preview = result?.shopify_rate_preview;

  return (
    <div className="space-y-6 p-6" dir="rtl" data-testid="rate-calculator-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calculator className="h-8 w-8" />
            حاسبة أسعار الشحن — للموظفين
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            تحقَّق يدوياً من سعر الشحن لعميل في حال فشل الحساب التلقائي في Shopify.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Input form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>بيانات الطلب</CardTitle>
            <CardDescription>أدخل SKU + الكمية + الوزن من Shopify.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>دولة الوجهة</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>المنتجات</Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="h-4 w-4 ml-1" /> إضافة منتج
                </Button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 rounded-lg border bg-muted/30">
                  <div className="col-span-6">
                    <Label className="text-xs">SKU</Label>
                    <Input
                      value={it.sku}
                      onChange={e => updateItem(i, 'sku', e.target.value)}
                      placeholder="MTT315090004"
                      data-testid={`sku-input-${i}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">الكمية</Label>
                    <Input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={e => updateItem(i, 'quantity', e.target.value)}
                      data-testid={`qty-input-${i}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">الوزن (غ)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={it.grams}
                      onChange={e => updateItem(i, 'grams', e.target.value)}
                      placeholder="14520"
                      data-testid={`grams-input-${i}`}
                    />
                  </div>
                  <div className="col-span-1">
                    {items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(i)}
                        data-testid={`remove-item-${i}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                className="flex-1"
                onClick={handleCalculate}
                disabled={calculating}
                data-testid="calculate-btn"
              >
                {calculating ? (
                  <><Loader2 className="h-4 w-4 ml-2 animate-spin" /> جارٍ الحساب…</>
                ) : (
                  <><Calculator className="h-4 w-4 ml-2" /> احسب السعر</>
                )}
              </Button>
              <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
                إعادة تعيين
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Result panel */}
        <div className="lg:col-span-3 space-y-4">
          {!result && !calculating && (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>املأ النموذج واضغط "احسب السعر" لعرض النتيجة</p>
              </CardContent>
            </Card>
          )}

          {result && (
            <>
              {/* Headline price card */}
              {result.ok && preview ? (
                <Card className="border-emerald-500/40 bg-emerald-500/5">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 className="h-5 w-5" />
                        السعر المحسوب (هذا ما يراه العميل في Shopify)
                      </CardTitle>
                      <StatusBadge status={result.status} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-5xl font-bold text-emerald-700" data-testid="total-price">
                      {calc.total_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ر.س
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <Stat label="المنطقة" value={`Zone ${calc.zone}`} />
                      <Stat label="الوزن المحتسب" value={`${calc.total_weight_kg} كجم`} />
                      <Stat label="السعر الأساسي (مع الربح + Fuel)" value={`${calc.base_rate} ر.س`} />
                      <Stat label="رسم الحرب" value={`${calc.war_surcharge} ر.س`} />
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-rose-500/40 bg-rose-500/5">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-rose-700">
                        <AlertTriangle className="h-5 w-5" />
                        لا يمكن حساب السعر
                      </CardTitle>
                      <StatusBadge status={result.status} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-rose-700" data-testid="error-message">
                      {calc?.error || result.error || 'حدث خطأ غير متوقَّع.'}
                    </p>
                    {calc?.missing_skus?.length > 0 && (
                      <div className="mt-3 text-sm">
                        <strong>SKUs غير موجودة في قاعدة البيانات:</strong>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {calc.missing_skus.map(sku => (
                            <Badge key={sku} variant="outline" className="border-rose-300">{sku}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Breakdown per SKU */}
              {calc?.breakdown?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">تفاصيل كل منتج</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {calc.breakdown.map((b, i) => (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border text-sm ${
                            b.found ? 'border-emerald-500/30 bg-emerald-500/5'
                                    : 'border-amber-500/30 bg-amber-500/5'
                          }`}
                          data-testid={`breakdown-${i}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono font-semibold">{b.sku || '—'}</span>
                            <div className="flex gap-2 items-center">
                              <Badge variant="outline">×{b.quantity}</Badge>
                              {b.found
                                ? <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30">موجود في DB</Badge>
                                : <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/30">
                                    {b.reason === 'sku_not_in_db_used_grams' ? 'استخدام grams من Shopify' : 'غير موجود'}
                                  </Badge>
                              }
                            </div>
                          </div>
                          {b.found ? (
                            <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                              <span>فعلي: {b.actual_lbs} lb</span>
                              <span>حجمي: {b.dim_lbs} lb</span>
                              <span>محتسب: {b.chargeable_lbs} lb</span>
                              <span className="font-semibold text-foreground">= {b.weight_kg} كجم</span>
                            </div>
                          ) : b.reason === 'sku_not_in_db_used_grams' ? (
                            <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                              <span>فعلي: {b.actual_kg_per_unit} كجم/وحدة</span>
                              <span>حجمي مقدَّر: {b.dim_kg_per_unit_estimated} كجم/وحدة</span>
                              <span>محتسب/وحدة: {b.chargeable_kg_per_unit} كجم</span>
                              <span className="font-semibold text-foreground">= {b.weight_kg} كجم</span>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">لا يوجد grams — لا يمكن حساب السعر</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Shopify preview JSON */}
              {preview && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">استجابة Shopify JSON</CardTitle>
                    <Button variant="outline" size="sm" onClick={copyResult} data-testid="copy-json-btn">
                      <Copy className="h-3 w-3 ml-1" /> نسخ
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-muted p-3 rounded overflow-x-auto text-left" dir="ltr">
{JSON.stringify(preview, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-muted-foreground text-xs">{label}</div>
    <div className="font-semibold">{value}</div>
  </div>
);

export default RateCalculator;
